import React from 'react';
import { signOutCurrentSession } from './signOut';

// Signs an unattended browser out instead of letting it sit on a live session all night. Applies
// to both session types; signOutCurrentSession picks the right way to end each. For an internal
// (Entra) session nothing server-side could do this anyway — the token is stateless — which is why
// it lives in the client; a customer session does have a revocable refresh token, and ending it
// here revokes that too.
//
// 120 minutes, Jeremy's call (2026-08-28). There's no ceiling imposed by Entra: MSAL would have gone
// on renewing the token silently for as long as the tab stayed open, which is the behavior this
// replaces.
const IDLE_LIMIT_MS = 120 * 60 * 1000;
const WARNING_MS = 60 * 1000;

// localStorage, not sessionStorage: MSAL's per-tab cache doesn't apply here, and someone working in
// one tab must not be signed out by a second idle tab of the same app.
const ACTIVITY_KEY = 'psops.lastActivity';
const ACTIVITY_WRITE_INTERVAL_MS = 30 * 1000;

// Read once by the sign-in screen, so a sign-out nobody asked for doesn't look like a bug.
export const IDLE_SIGN_OUT_KEY = 'psops.signedOutIdle';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];
// Once the countdown is on screen, a bumped desk shouldn't silently buy another two hours — only a
// deliberate action counts from that point on.
const PASSIVE_EVENTS = new Set(['mousemove', 'wheel', 'scroll']);

function readSharedActivity() {
  try {
    return Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
  } catch {
    return 0; // private mode
  }
}

export default function IdleLogout() {
  const lastActivityRef = React.useRef(Date.now());
  const lastWriteRef = React.useRef(0);
  const warningRef = React.useRef(false);
  const signingOutRef = React.useRef(false);
  // Non-null only while the countdown is showing, which is also what renders the dialog.
  const [msLeft, setMsLeft] = React.useState(null);

  const markActive = React.useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // mousemove fires constantly and localStorage writes are synchronous, hence the throttle. A
    // 30s lag is nothing against a 2-hour limit.
    if (now - lastWriteRef.current > ACTIVITY_WRITE_INTERVAL_MS) {
      lastWriteRef.current = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {
        /* private mode */
      }
    }
  }, []);

  React.useEffect(() => {
    const onActivity = (event) => {
      if (warningRef.current && PASSIVE_EVENTS.has(event.type)) return;
      markActive();
    };
    // Capture phase, so a component that stops propagation can't blind the idle timer.
    const options = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, options));
    return () =>
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, { capture: true })
      );
  }, [markActive]);

  React.useEffect(() => {
    const id = setInterval(() => {
      if (signingOutRef.current) return;

      const lastActivity = Math.max(lastActivityRef.current, readSharedActivity());
      const remaining = IDLE_LIMIT_MS - (Date.now() - lastActivity);

      if (remaining <= 0) {
        signingOutRef.current = true;
        try {
          sessionStorage.setItem(IDLE_SIGN_OUT_KEY, '1');
        } catch {
          /* private mode */
        }
        // Survives the round trip (to Entra, or to the portal's sign-in screen): same tab, same
        // origin, so the flag is still there when we land back on the sign-in screen.
        signOutCurrentSession().catch(() => {
          signingOutRef.current = false;
        });
        return;
      }

      const showWarning = remaining <= WARNING_MS;
      warningRef.current = showWarning;
      setMsLeft(showWarning ? remaining : null);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const staySignedIn = () => {
    warningRef.current = false;
    markActive();
    setMsLeft(null);
  };

  if (msLeft === null) return null;

  return (
    <div
      className="modal-overlay"
      // Above the help drawer (900) and the notification panel (940) — this one can't be buried.
      style={{ zIndex: 1000, alignItems: 'center' }}
      role="alertdialog"
      aria-modal="true"
      aria-label="Session about to expire"
    >
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <span className="modal-title">Still there?</span>
        </div>
        <div className="modal-body" style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6 }}>
          You've been inactive for a while, so you'll be signed out in{' '}
          <strong style={{ color: 'var(--text-dark)' }}>{Math.max(0, Math.ceil(msLeft / 1000))}s</strong>.
          Anything unsaved on this page will be lost.
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={staySignedIn} autoFocus>
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
